import actions from 'actions'
import now from 'performance-now'
import type { Dispatch } from 'redux'

import type { ThunkExperiments } from '@library/experimentation'
import { boxFetcher } from '@library/menu-service'
import { instrumentBrowserError, BROWSER_ERROR_KEYS } from '@library/observability/browser'

import { actionTypes } from 'actions/actionTypes'
import { getMenuCutoffUntil } from 'actions/basket'
import { boxSummaryDeliveryDaysLoad } from 'actions/boxSummary'
import { menuCutoffUntilReceive, menuLoadComplete } from 'actions/menu'
import { boxesDataReceived } from 'actions/menu-boxes/boxes'
import { menuServiceDataReceived } from 'actions/menuService'
import { isChosenSlotAvailable } from 'routes/Menu/selectors/boxSummary'
import { getIsAdmin, getIsAuthenticated } from 'selectors/auth'
import { getBasketDate, getNumPortions } from 'selectors/basket'
import { getSessionId } from 'selectors/cookies'
import { getMenuCutoffUntil as getMenuCutOffUntilSelector } from 'selectors/root'
import { getLandingDay } from 'utils/deliveries'

import type { AddRecipeFn } from '../domains/basket'
import { getAvailableSignupMotivations } from '../selectors/getAvailableSignupMotivations'
import { fetchMenuV3 } from './fetchMenuServiceV3'
import type { GetState, Query } from './types'
import { parseNumPortions, setSlotFromIds } from './utils'

const chooseFirstDate = () => (dispatch: Dispatch<any>, getState: GetState) => {
  const { date } = getLandingDay(getState())

  return dispatch(actions.basketDateChange(date))
}

const beforeMenuLoad = (query: Query) => (dispatch: Dispatch<any>, getState: GetState) => {
  if (getState().basket.get('orderId')) {
    const shippingAddresses = getState().user.get('shippingAddresses')

    const addressToSelect =
      shippingAddresses.find((address: any) => address.get('shippingDefault') === true) ||
      shippingAddresses.first()

    dispatch(actions.basketReset(addressToSelect))
  }

  if (
    query.day_id ||
    query.slot_id ||
    getBasketDate(getState()) ||
    getState().basket.get('slotId')
  ) {
    dispatch(setSlotFromIds(query.slot_id, query.day_id))
  } else if (!getBasketDate(getState())) {
    chooseFirstDate()(dispatch, getState)
  }

  if (query.num_portions) {
    dispatch(actions.setBasketNumPortion(query.num_portions))
  }

  if (query.postcode && !getState().basket.get('postcode')) {
    dispatch(actions.basketPostcodeChangePure(query.postcode))
  }
}

function handleExpiredBasketDate({
  dispatch,
  getState,
}: {
  dispatch: Dispatch<any>
  getState: GetState
}) {
  if (!isChosenSlotAvailable(getState())) {
    const { date } = getLandingDay(getState(), {
      useBasketDate: false,
    })

    dispatch(actions.basketDateChange(date))
  }
}

// eslint-disable-next-line import/no-default-export
export function fetchData(
  { query, params }: { query: Query; params: any },
  force: boolean,
  background: any,
  { addRecipe }: { addRecipe: AddRecipeFn },
) {
  return async (dispatch: Dispatch<any>, getState: GetState, { experiments }: ThunkExperiments) => {
    const startTime = now()

    const isPending = getState().pending && getState().pending.get(actionTypes.MENU_FETCH_DATA)
    const isPreviewMode = !!(query && query['preview[auth_user_id]'])

    if (!isPreviewMode && isPending) {
      return
    }

    dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, true))
    dispatch(actions.error(actionTypes.MENU_FETCH_DATA, false))

    const isAuthenticated = getIsAuthenticated(getState())

    if (params.orderId) {
      await dispatch(actions.userLoadOrders(true))
      dispatch(actions.basketIdChange(params.orderId))
    } else {
      const isAdmin = getIsAdmin(getState())

      if (isAuthenticated && !isAdmin) {
        await dispatch(actions.userLoadOrders(true))
      }
    }

    let stage = 'initialising'
    let isErrorInstrumented = false

    try {
      stage = 'ensure-menu-cutoff'
      let cutOffUntil = getMenuCutOffUntilSelector(getState())
      if (!cutOffUntil) {
        // This will fetch from /active endpoint
        cutOffUntil = await getMenuCutoffUntil()
        dispatch(menuCutoffUntilReceive(cutOffUntil))
        await dispatch(boxSummaryDeliveryDaysLoad(undefined, cutOffUntil))
      }

      const overrideNumPortions = parseNumPortions(query.selected_box_size)
      // we need the delivery days to calculate the date to pass to the menu API based on the delivery date using the slots data
      if (params.orderId) {
        const { auth, user } = getState()
        if (auth.get('isAuthenticated') && !user.get('email') && !auth.get('isAdmin')) {
          stage = 'load-user-data'
          await dispatch(actions.userLoadData())
        }
        stage = 'load-order-details'
        await dispatch(actions.menuLoadOrderDetails(params.orderId, addRecipe, overrideNumPortions))
      } else {
        stage = 'pre-menu-load'
        beforeMenuLoad(query)(dispatch, getState)
        handleExpiredBasketDate({ dispatch, getState })
      }

      const deliveryDate = getBasketDate(getState())
      const sessionId = getSessionId()

      const boxesPromise = boxFetcher({ sessionId })
        ?.then((boxesResponse) => {
          dispatch(boxesDataReceived(boxesResponse))
        })
        .catch((boxesError) => {
          instrumentBrowserError(BROWSER_ERROR_KEYS.FAILED_TO_LOAD_BOXES, {
            source: 'fetchData',
            actionType: 'boxFetcher',
            stage,
            sessionId,
            errorMessage: boxesError instanceof Error ? boxesError.message : 'Not an Error object',
          })

          isErrorInstrumented = true

          throw boxesError
        })

      const numPortions = overrideNumPortions || getNumPortions(getState())
      const { variationKey: recipeGroupingsVariationKey } =
        isAuthenticated && experiments.decide('turnips_ofx_recipe_groupings')

      const motivationIds = getAvailableSignupMotivations(getState, { experiments })

      stage = 'fetch-menu'
      try {
        await dispatch(
          fetchMenuV3(
            {
              deliveryDate,
              numPortions,
              includeRecipeGroups: recipeGroupingsVariationKey === 't1',
              ...(motivationIds && { motivationIds }),
              'preview[menu_id]': query['preview[menu_id]'],
              'preview[auth_user_id]': query['preview[auth_user_id]'],
              'preview[expiry]': query['preview[expiry]'],
              'preview[signature]': query['preview[signature]'],
            },
            isPreviewMode,
          ),
        )
      } catch (menuLoadError) {
        instrumentBrowserError(BROWSER_ERROR_KEYS.FAILED_TO_LOAD_MENU, {
          source: 'fetchData',
          actionType: 'fetchMenuV3',
          stage,
          deliveryDate,
          numPortions,
          recipeGroupingsVariationKey,
          motivationIds,
          isPreviewMode,
          errorMessage:
            menuLoadError instanceof Error ? menuLoadError.message : 'Not an Error object',
        })

        isErrorInstrumented = true

        throw menuLoadError
      }

      dispatch(menuServiceDataReceived()) // this dispatches tracking event, remove after analytics started to use v3 alternative

      stage = 'menu-load'
      dispatch(actions.menuLoadMenu())

      stage = 'await-boxes'
      await boxesPromise

      dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, false))

      const timeTaken = Math.round(now() - startTime)
      stage = 'complete'
      dispatch(menuLoadComplete(timeTaken, true))
    } catch (e) {
      const errorMessage =
        (e instanceof Error ? e.message : String(e)) || 'Unknown fetch menu error'

      if (!isErrorInstrumented) {
        instrumentBrowserError(BROWSER_ERROR_KEYS.FAILED_TO_LOAD_MENU, {
          source: 'fetchData',
          actionType: 'fetchData',
          isAuthenticated: getIsAuthenticated(getState()),
          deliveryDate: getBasketDate(getState()),
          orderId: params.orderId,
          errorMessage,
          stage,
          errorTag: 'MENU_LOAD_FAIL',
        })
      }

      dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, false))
      dispatch(actions.error(actionTypes.MENU_FETCH_DATA, true))
      throw e
    }
  }
}
